import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import { Zap, ShieldCheck, ArrowLeft, CreditCard, CheckCircle2 } from "lucide-react";
import SeoCopy from "@/components/SeoCopy";

export default function QuickPay() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const done = window.location.pathname.endsWith("/done");
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");

  const [communities, setCommunities] = useState([]);
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    community: "",
    residence_type: "apartment",
    building: "",
    unit: "",
    bags: 1,
    tip_amount: 0,
    custom_tip: "",
    bags_tied: false,
    no_hazardous: false,
  });

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings`).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/communities/active`).then((r) => setCommunities(r.data || [])).catch(() => {});
  }, []);

  if (done) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="card-clay p-8">
          <CheckCircle2 size={48} strokeWidth={2.5} className="text-green-600 mx-auto"/>
          <h1 className="font-fredoka text-3xl font-bold text-slate-900 mt-3" data-testid="quickpay-done-title">Paid in full!</h1>
          <p className="text-slate-600 mt-2">The kids have your payment. Have a great day!</p>
          <button onClick={() => navigate("/")} className="btn-push-green w-full mt-5 py-3">Back to home</button>
          {sessionId && <div className="text-[10px] text-slate-300 mt-3 font-mono break-all" data-testid="quickpay-session-id">{sessionId}</div>}
          {orderId && <div className="text-[10px] text-slate-300 mt-1 font-mono break-all" data-testid="quickpay-order-id">{orderId}</div>}
        </div>
      </div>
    );
  }

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selected = communities.find((c) => c.name === form.community);

  const computeTotal = () => {
    if (!settings) return 0;
    const b = Math.max(1, parseInt(form.bags || 1));
    let base = b === 1 ? settings.price_1_bag : b === 2 ? settings.price_2_bags : settings.price_2_bags + (b - 2) * settings.price_extra_bag;
    const tip = form.tip_amount === "custom" ? parseFloat(form.custom_tip || 0) || 0 : parseFloat(form.tip_amount || 0) || 0;
    return Math.round((base + tip) * 100) / 100;
  };
  const total = computeTotal();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.community) { toast.error("Pick the community."); return; }
    if (!form.bags_tied || !form.no_hazardous) { toast.error("Please confirm bags are tied and no hazardous waste."); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        bags: parseInt(form.bags),
        tip_amount: form.tip_amount === "custom" ? parseFloat(form.custom_tip || 0) || 0 : parseFloat(form.tip_amount || 0) || 0,
        origin_url: window.location.origin,
      };
      delete payload.custom_tip;
      const { data } = await axios.post(`${BACKEND_URL}/api/quick-pay`, payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={16}/> Back
      </button>

      <div className="card-clay p-5 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 flex items-center gap-3 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-amber-400 text-amber-950 border-b-4 border-amber-600 flex items-center justify-center">
          <Zap size={22} strokeWidth={2.5}/>
        </div>
        <div>
          <h1 className="font-fredoka text-2xl font-bold text-slate-900" data-testid="quickpay-title">Quick Pay — Kids Are Here</h1>
          <p className="text-xs text-slate-700 mt-0.5">For paying at the door right now. No scheduling — just pay and go.</p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="card-clay p-5 space-y-3">
          <h2 className="font-fredoka font-bold text-lg">Your details</h2>
          <input className="input-clay h-11 px-3 w-full" placeholder="Full name" required value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="qp-name"/>
          <div className="grid sm:grid-cols-2 gap-3">
            <input type="tel" className="input-clay h-11 px-3 w-full" placeholder="Phone" required value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="qp-phone"/>
            <input type="email" className="input-clay h-11 px-3 w-full" placeholder="Email (for receipt)" required value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="qp-email"/>
          </div>
        </div>

        <div className="card-clay p-5 space-y-3">
          <h2 className="font-fredoka font-bold text-lg">Where are you?</h2>
          <select className="input-clay h-11 px-3 w-full bg-white" required value={form.community} onChange={(e) => { upd("community", e.target.value); upd("building", ""); }} data-testid="qp-community">
            <option value="">Select community…</option>
            {communities.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          {communities.length === 0 && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              Bag Dash Kids is currently serving select communities only. Please check back soon or request your community to be added.
            </div>
          )}
          {selected && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => { upd("residence_type", "apartment"); upd("building", ""); }} className={`rounded-xl px-3 py-2.5 border-2 text-sm font-semibold ${form.residence_type === "apartment" ? "bg-green-500 text-white border-green-700" : "bg-white border-slate-200"}`} data-testid="qp-apartment">🏢 Apartment</button>
                <button type="button" disabled={!selected.allows_townhomes} onClick={() => { upd("residence_type", "townhome"); upd("building", ""); }} className={`rounded-xl px-3 py-2.5 border-2 text-sm font-semibold ${form.residence_type === "townhome" ? "bg-green-500 text-white border-green-700" : selected.allows_townhomes ? "bg-white border-slate-200" : "bg-slate-50 border-slate-200 text-slate-400 cursor-not-allowed"}`} data-testid="qp-townhome">🏘️ Townhome</button>
              </div>
              {form.residence_type === "apartment" ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  <select className="input-clay h-11 px-3 w-full bg-white" required value={form.building} onChange={(e) => upd("building", e.target.value)} data-testid="qp-building">
                    <option value="">Building…</option>
                    {(selected.buildings || []).map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <input className="input-clay h-11 px-3 w-full" placeholder="Unit #" required value={form.unit} onChange={(e) => upd("unit", e.target.value)} data-testid="qp-unit"/>
                </div>
              ) : (
                <input className="input-clay h-11 px-3 w-full" placeholder="Townhome address or unit identifier" required value={form.building} onChange={(e) => upd("building", e.target.value)} data-testid="qp-townhome-addr"/>
              )}
            </>
          )}
        </div>

        <div className="card-clay p-5 space-y-3">
          <h2 className="font-fredoka font-bold text-lg">How many bags?</h2>
          <input type="number" min={1} max={20} className="input-clay h-11 px-3 w-full" value={form.bags} onChange={(e) => upd("bags", e.target.value)} data-testid="qp-bags"/>
          <div className="text-xs text-slate-500">Pricing matches our regular rate. Tip optional.</div>
          <div className="grid grid-cols-5 gap-1.5">
            {[0, 2, 6, 10, "custom"].map((t) => (
              <button key={String(t)} type="button" onClick={() => upd("tip_amount", t)} className={`rounded-lg px-2 py-1.5 text-xs font-semibold border-2 ${form.tip_amount === t ? "bg-green-500 text-white border-green-700" : "bg-white border-slate-200 text-slate-700"}`} data-testid={`qp-tip-${t}`}>
                {t === "custom" ? "Custom" : t === 0 ? "No tip" : `$${t}`}
              </button>
            ))}
          </div>
          {form.tip_amount === "custom" && (
            <input type="number" min={0} className="input-clay h-10 px-3 w-full" placeholder="Custom tip $" value={form.custom_tip} onChange={(e) => upd("custom_tip", e.target.value)}/>
          )}
        </div>

        <div className="card-clay p-5 space-y-2 text-sm">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={form.bags_tied} onChange={(e) => upd("bags_tied", e.target.checked)} data-testid="qp-bags-tied" required/>
            <span>Bags are tied securely.</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={form.no_hazardous} onChange={(e) => upd("no_hazardous", e.target.checked)} data-testid="qp-no-haz" required/>
            <span>No hazardous, sharp, leaking, or oversized items.</span>
          </label>
        </div>

        <div className="card-clay p-5 sticky bottom-2 flex items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Pay now</div>
            <div className="font-fredoka text-3xl font-bold text-slate-900" data-testid="qp-total">${total.toFixed(2)}</div>
          </div>
          <button type="submit" disabled={submitting} className="btn-push-green px-5 py-3 inline-flex items-center gap-2 disabled:opacity-60" data-testid="qp-submit">
            <CreditCard size={16}/>{submitting ? "Redirecting…" : `Pay $${total.toFixed(2)}`}
          </button>
        </div>
        <div className="text-[11px] text-slate-500 flex items-center gap-1 justify-center mt-1">
          <ShieldCheck size={12} className="text-green-600"/> Stripe Checkout · we never store your card.
        </div>
      </form>
      <SeoCopy
        title="What is Quick Pay?"
        links={[
          { to: "/schedule", label: "Schedule a pickup" },
          { to: "/about", label: "Meet the Kids" },
          { to: "/rules", label: "What we pick up" },
          { to: "/policies", label: "Pricing & policies" },
        ]}
      >
        <p>Quick Pay is the no-login way to pay <strong>Big Sib Josiah</strong> or <strong>Lil Sib Jayla</strong> on the spot when they&apos;ve already shown up at your door for a bag. Enter your name, email, the number of bags, and an optional tip — Stripe handles the rest. A receipt lands in your inbox within minutes.</p>
        <p>For scheduled or recurring weekly service, use the <strong>Schedule</strong> page so we can plan the kids&apos; route in advance and reserve a window for you.</p>
      </SeoCopy>
    </div>
  );
}
